import React, { useEffect, useState } from "react";
import "../../assets/styles/card.css";
import "../../assets/styles/toggle.css";
import BASE_URL from "../../config";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Its own top-level settings section (see settingsPage/sidebar.js) rather than
// living inside System Preferences -- pulled out of systemPreferences.js,
// which previously hosted this block directly, to give email its own place
// in the settings sidebar between System Preferences and Personal Settings.
function EmailSettings({ customerId = null }) {
  const isAdminMode = Boolean(customerId);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [gmailEmail, setGmailEmail] = useState("");
  const [outlookConnected, setOutlookConnected] = useState(false);
  const [outlookEmail, setOutlookEmail] = useState("");
  const [imapConnected, setImapConnected] = useState(false);
  const [imapEmail, setImapEmail] = useState("");
  const [showImapForm, setShowImapForm] = useState(false);
  const [imapSubmitting, setImapSubmitting] = useState(false);
  const [imapForm, setImapForm] = useState({
    email_address: "",
    password: "",
    imap_host: "",
    imap_port: 993,
    smtp_host: "",
    smtp_port: 587,
  });
  const [loading, setLoading] = useState(true);

  // Only one email provider can be connected at a time -- the portal shows
  // whichever one actually is, and hides the "connect" options once any of
  // them is active.
  const anyEmailConnected = gmailConnected || outlookConnected || imapConnected;
  const connectedEmailAddress = gmailConnected
    ? gmailEmail
    : outlookConnected
    ? outlookEmail
    : imapEmail;
  const connectedEmailProviderLabel = gmailConnected
    ? "Gmail"
    : outlookConnected
    ? "Outlook"
    : "Email";

  useEffect(() => {
    const checkGmailConnection = async () => {
      if (isAdminMode) return;
      try {
        const token = localStorage.getItem("jwt_token");
        const res = await fetch(`${BASE_URL}/api/auth/google/status/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setGmailConnected(data.connected);
          if (data.connected && data.email) {
            setGmailEmail(data.email);
          }
        } else {
          setGmailConnected(false);
        }
      } catch (err) {
        console.error("Error checking Gmail connection:", err);
        setGmailConnected(false);
      }
    };

    const checkOutlookConnection = async () => {
      if (isAdminMode) return;
      try {
        const token = localStorage.getItem("jwt_token");
        const res = await fetch(`${BASE_URL}/api/auth/outlook/status/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setOutlookConnected(data.connected);
          if (data.connected && data.email) {
            setOutlookEmail(data.email);
          }
        } else {
          setOutlookConnected(false);
        }
      } catch (err) {
        console.error("Error checking Outlook connection:", err);
        setOutlookConnected(false);
      }
    };

    const checkImapConnection = async () => {
      if (isAdminMode) return;
      try {
        const token = localStorage.getItem("jwt_token");
        const res = await fetch(`${BASE_URL}/api/auth/imap/status/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setImapConnected(data.connected);
          if (data.connected && data.email) {
            setImapEmail(data.email);
          }
        } else {
          setImapConnected(false);
        }
      } catch (err) {
        console.error("Error checking email connection:", err);
        setImapConnected(false);
      } finally {
        setLoading(false);
      }
    };

    checkGmailConnection();
    checkOutlookConnection();
    checkImapConnection();
  }, [customerId, isAdminMode]);

  const connectGmail = async () => {
    try {
      const token = localStorage.getItem("jwt_token");
      const res = await fetch(`${BASE_URL}/api/auth/google/init/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.auth_url;
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.error || "Failed to connect Gmail.");
      }
    } catch (err) {
      console.error("Error connecting Gmail:", err);
      toast.error("Failed to connect Gmail.");
    }
  };

  const disconnectGmail = async () => {
    try {
      const token = localStorage.getItem("jwt_token");
      const res = await fetch(`${BASE_URL}/api/auth/google/disconnect/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success("Gmail disconnected successfully!");
        setGmailConnected(false);
        setGmailEmail("");
      } else {
        toast.error("Failed to disconnect Gmail.");
      }
    } catch (err) {
      console.error("Error disconnecting Gmail:", err);
      toast.error("Error disconnecting Gmail.");
    }
  };

  const connectOutlook = async () => {
    try {
      const token = localStorage.getItem("jwt_token");
      const res = await fetch(`${BASE_URL}/api/auth/outlook/init/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.auth_url;
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.error || "Failed to connect Outlook.");
      }
    } catch (err) {
      console.error("Error connecting Outlook:", err);
      toast.error("Failed to connect Outlook.");
    }
  };

  const disconnectOutlook = async () => {
    try {
      const token = localStorage.getItem("jwt_token");
      const res = await fetch(`${BASE_URL}/api/auth/outlook/disconnect/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success("Outlook disconnected successfully!");
        setOutlookConnected(false);
        setOutlookEmail("");
      } else {
        toast.error("Failed to disconnect Outlook.");
      }
    } catch (err) {
      console.error("Error disconnecting Outlook:", err);
      toast.error("Error disconnecting Outlook.");
    }
  };

  // Sensible IMAP/SMTP defaults for well-known providers -- mirrors
  // integrations/imap_auth.py's KNOWN_PROVIDER_DEFAULTS on the backend, just
  // to save the user typing. Any other domain still works if they fill the
  // host/port fields in themselves.
  const IMAP_PROVIDER_DEFAULTS = {
    "yahoo.com": { imap_host: "imap.mail.yahoo.com", imap_port: 993, smtp_host: "smtp.mail.yahoo.com", smtp_port: 587 },
    "icloud.com": { imap_host: "imap.mail.me.com", imap_port: 993, smtp_host: "smtp.mail.me.com", smtp_port: 587 },
    "me.com": { imap_host: "imap.mail.me.com", imap_port: 993, smtp_host: "smtp.mail.me.com", smtp_port: 587 },
    "aol.com": { imap_host: "imap.aol.com", imap_port: 993, smtp_host: "smtp.aol.com", smtp_port: 587 },
  };

  const handleImapEmailChange = (value) => {
    const domain = value.split("@")[1]?.toLowerCase().trim();
    const defaults = domain ? IMAP_PROVIDER_DEFAULTS[domain] : null;
    setImapForm((prev) => ({
      ...prev,
      email_address: value,
      ...(defaults && !prev.imap_host ? defaults : {}),
    }));
  };

  const connectImap = async () => {
    if (!imapForm.email_address || !imapForm.password || !imapForm.imap_host || !imapForm.smtp_host) {
      toast.error("Email, app password, IMAP host, and SMTP host are all required.");
      return;
    }
    setImapSubmitting(true);
    try {
      const token = localStorage.getItem("jwt_token");
      const res = await fetch(`${BASE_URL}/api/auth/imap/connect/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(imapForm),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success("Email account connected!");
        setImapConnected(true);
        setImapEmail(data.email || imapForm.email_address);
        setShowImapForm(false);
        setImapForm({ email_address: "", password: "", imap_host: "", imap_port: 993, smtp_host: "", smtp_port: 587 });
      } else {
        toast.error(data.error || "Failed to connect email account.");
      }
    } catch (err) {
      console.error("Error connecting email account:", err);
      toast.error("Failed to connect email account.");
    } finally {
      setImapSubmitting(false);
    }
  };

  const disconnectImap = async () => {
    try {
      const token = localStorage.getItem("jwt_token");
      const res = await fetch(`${BASE_URL}/api/auth/imap/disconnect/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success("Email account disconnected successfully!");
        setImapConnected(false);
        setImapEmail("");
      } else {
        toast.error("Failed to disconnect email account.");
      }
    } catch (err) {
      console.error("Error disconnecting email account:", err);
      toast.error("Error disconnecting email account.");
    }
  };

  const disconnectConnectedEmail = () => {
    if (gmailConnected) return disconnectGmail();
    if (outlookConnected) return disconnectOutlook();
    if (imapConnected) return disconnectImap();
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      <div className="pref-card-container">
        <h2 className="pref-card-title">Email Settings</h2>
        <p className="pref-card-description">
          {isAdminMode
            ? "This customer's connected email account for reading and sending messages by voice."
            : "Connect an email account to read and send messages by voice. Only one account can be connected at a time -- disconnect it first if you want to switch providers."}
        </p>
        {loading ? (
          <p className="pref-card-loading">Loading...</p>
        ) : isAdminMode ? (
          <div className="pref-row">
            <span className="pref-card-mode" aria-live="polite">
              Email Integration: &nbsp;
              <strong>
                {anyEmailConnected
                  ? `Connected via ${connectedEmailProviderLabel}${connectedEmailAddress ? ` (${connectedEmailAddress})` : ""}`
                  : "Pending Connection"}
              </strong>
            </span>
          </div>
        ) : (
          <div className="pref-row" style={{ flexDirection: "column", alignItems: "stretch", gap: "16px" }}>
            {/* Status text and the connect/disconnect controls each get their own
                full-width row instead of sharing one flex line -- side-by-side
                caused the label to wrap and visually collide with the buttons
                once both couldn't fit the card's width. */}
            <div style={{ width: "100%" }}>
              <span className="pref-card-mode" aria-live="polite" style={{ display: "block" }}>
                Email Integration: &nbsp;
                <strong>
                  {anyEmailConnected
                    ? `Connected via ${connectedEmailProviderLabel}${connectedEmailAddress ? ` (${connectedEmailAddress})` : ""}`
                    : "Pending Connection"}
                </strong>
              </span>
            </div>

            {anyEmailConnected ? (
              <div style={{ width: "100%" }}>
                <button
                  className="pref-btn-disconnect"
                  onClick={disconnectConnectedEmail}
                  aria-label={`Disconnect ${connectedEmailProviderLabel}${connectedEmailAddress ? ` (${connectedEmailAddress})` : ""}`}
                >
                  Disconnect
                </button>
              </div>
            ) : !showImapForm ? (
              <div
                role="group"
                aria-label="Connect an email provider"
                style={{ display: "flex", flexWrap: "wrap", gap: "8px", width: "100%" }}
              >
                <button className="pref-btn-connect" onClick={connectGmail}>
                  Connect Gmail
                </button>
                <button className="pref-btn-connect" onClick={connectOutlook}>
                  Connect Outlook
                </button>
                <button className="pref-btn-connect" onClick={() => setShowImapForm(true)}>
                  Other Email
                </button>
              </div>
            ) : null}

            {!anyEmailConnected && showImapForm && (
              <div
                role="group"
                aria-label="Connect another email account with an app password"
                style={{ width: "100%", padding: "12px", border: "1px solid #333", borderRadius: "8px" }}
              >
                <p style={{ margin: "0 0 8px", fontSize: "0.9em", opacity: 0.8 }}>
                  Connect any other email provider using an app password (Yahoo, iCloud, a work mailbox, etc).
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "8px" }}>
                  <div>
                    <label htmlFor="imap-email-address" className="pref-field-label">
                      Email address
                    </label>
                    <input
                      id="imap-email-address"
                      type="email"
                      placeholder="you@example.com"
                      className="pref-select"
                      value={imapForm.email_address}
                      onChange={(e) => handleImapEmailChange(e.target.value)}
                    />
                  </div>
                  <div>
                    <label htmlFor="imap-password" className="pref-field-label">
                      App password
                    </label>
                    <input
                      id="imap-password"
                      type="password"
                      placeholder="App password"
                      className="pref-select"
                      value={imapForm.password}
                      onChange={(e) => setImapForm((prev) => ({ ...prev, password: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label htmlFor="imap-host" className="pref-field-label">
                      IMAP host
                    </label>
                    <input
                      id="imap-host"
                      type="text"
                      placeholder="e.g. imap.example.com"
                      className="pref-select"
                      value={imapForm.imap_host}
                      onChange={(e) => setImapForm((prev) => ({ ...prev, imap_host: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label htmlFor="imap-port" className="pref-field-label">
                      IMAP port
                    </label>
                    <input
                      id="imap-port"
                      type="number"
                      placeholder="993"
                      className="pref-select"
                      value={imapForm.imap_port}
                      onChange={(e) => setImapForm((prev) => ({ ...prev, imap_port: parseInt(e.target.value, 10) || "" }))}
                    />
                  </div>
                  <div>
                    <label htmlFor="smtp-host" className="pref-field-label">
                      SMTP host
                    </label>
                    <input
                      id="smtp-host"
                      type="text"
                      placeholder="e.g. smtp.example.com"
                      className="pref-select"
                      value={imapForm.smtp_host}
                      onChange={(e) => setImapForm((prev) => ({ ...prev, smtp_host: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label htmlFor="smtp-port" className="pref-field-label">
                      SMTP port
                    </label>
                    <input
                      id="smtp-port"
                      type="number"
                      placeholder="587"
                      className="pref-select"
                      value={imapForm.smtp_port}
                      onChange={(e) => setImapForm((prev) => ({ ...prev, smtp_port: parseInt(e.target.value, 10) || "" }))}
                    />
                  </div>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button className="pref-btn-connect" onClick={connectImap} disabled={imapSubmitting}>
                    {imapSubmitting ? "Connecting..." : "Connect"}
                  </button>
                  <button
                    className="pref-btn-disconnect"
                    onClick={() => setShowImapForm(false)}
                    disabled={imapSubmitting}
                    aria-label="Cancel connecting another email account"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default EmailSettings;
